// src/components/Inputs/DynamicCheckbox.jsx

// TODO: Use the provider instead

'use client'

import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { FormControl, FormControlLabel, FormLabel, Checkbox, TextField, Stack } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { usePreferences } from '@/contexts/ShopPreferences.context';
import { currencies } from '@/lib/currencies.utils';
import Grid from '@mui/material/GridLegacy';
import { useFilters } from '@/contexts/Filters.context';


export default function DynamicCheckbox({list, filterName, icon, translations, mode='currency', lang}) {

  const pathname = usePathname();
  const router = useRouter();
  const { currency, units } = usePreferences();
  const { getFilterRange, updateFilterRange, hasFilter, searchParams } = useFilters();
  const isUsingFilter = hasFilter(filterName);

  // Get filter range from useFilters
  const { min: minValue, max: maxValue } = getFilterRange(filterName);

  // Determine symbol based on mode and context
  const getSymbol = () => {
    if (mode === 'currency' && currency && currencies[currency]) {
      return currencies[currency].symbol;
    } else if (mode === 'units') {
      return 'mm';
    }
    return '';
  };

  const symbol = getSymbol();

  const getDefaultValues = () => {

    const filterValueFromURL = searchParams.get(filterName);

    const defaultEntries = {
      checkedValues: new Array(list.length).fill(false),
      filters: [],
    }

    if (filterValueFromURL) {

      const filters = filterValueFromURL.split(',').map((filter) => {
        const [key, value] = filter.split(':');
        return { [key]: parseInt(value, 10) };
      });

      const minFilter = filters.find((filter) => filter.min !== undefined);
      const maxFilter = filters.find((filter) => filter.max !== undefined);

      return {
        min: minFilter ? minFilter.min : '',
        max: maxFilter ? maxFilter.max : '',
        ...defaultEntries
      };

    } else {
      return {
        min: '',
        max: '',
        ...defaultEntries
      };
    }
  };

  const defaultValues = getDefaultValues();
  const [checkedValues, setCheckedValues] = useState(defaultValues.checkedValues);
  const [filters, setValueFilters] = useState(defaultValues.filters);
  const [maxValueInputBox, setMaxValueInputBox] = useState(defaultValues.max);
  const [minValueInputBox, setMinValueInputBox] = useState(defaultValues.min);

  useEffect(() => {
    if (!isUsingFilter) {
      setCheckedValues(new Array(list.length).fill(false));
      setValueFilters([]);
      setMinValueInputBox('');
      setMaxValueInputBox('');
    }
  }, [filterName, list.length, isUsingFilter]);

  const liftState = (valuesObj) => {

    const filteredValuesObj = Object.fromEntries(
      Object.entries(valuesObj || {}).filter(([, value]) => value !== '' && value !== 0 && value !== Infinity)
    );

    // Add currency or units based on mode if there's at least one value
    if (Object.keys(filteredValuesObj).length > 0) {
      if (mode === 'currency' && currency) {
        filteredValuesObj.currency = currency;
      } else if (mode === 'units' && units) {
        filteredValuesObj.units = units;
      }
    }

    const valuesParams = Object.entries(filteredValuesObj).map(([key, value]) => `${key}:${value}`).join(',');

    const existingParams = new URLSearchParams(searchParams);

    valuesParams ? existingParams.set(filterName, valuesParams) : existingParams.delete(filterName);

    const updatedQueryString = existingParams.toString();

    router.push(pathname + (updatedQueryString ? `?${updatedQueryString}` : ''), { scroll: false });
  };

  const handleCheckbox = (event, idx) => {

    const checkedValuesCopy = [...checkedValues];
    checkedValuesCopy[idx] = event.target.checked;
    setCheckedValues(checkedValuesCopy);

    const receivedArray = event.target.value.split(',').map(Number);
    const priceFiltersCopy = [...filters];

    if (priceFiltersCopy.includes(receivedArray[0]) && priceFiltersCopy.includes(receivedArray[1])){
      priceFiltersCopy.splice(priceFiltersCopy.indexOf(receivedArray[0]), 1);
      priceFiltersCopy.splice(priceFiltersCopy.indexOf(receivedArray[1]), 1);
      setValueFilters(priceFiltersCopy);
      liftState({
        min: priceFiltersCopy.length ? Math.min(...priceFiltersCopy) : '',
        max: priceFiltersCopy.length ? Math.max(...priceFiltersCopy) : '',
      });
    } else {
      const aggregateArray = [...filters, ...receivedArray];
      setValueFilters(aggregateArray);
      liftState({ min: Math.min(...aggregateArray), max: Math.max(...aggregateArray) });
    }
  }

  const handleInputBox = (event) => {

    const value = event.target.value;

    if (value.match('\\D')) return

    if (event.target.name === 'min'){
      setMinValueInputBox(value);

      liftState({
        min: value ? Number(value) : '',
        max: maxValueInputBox ? Number(maxValueInputBox) : '',
      });

    } else {
      setMaxValueInputBox(value);

      liftState({
        min: minValueInputBox ? Number(minValueInputBox) : '',
        max: value ? Number(value) : '',
      });
    }
  }

  const clearOutCheckboxes = () => {
    if (checkedValues.some(val => val)){
      setCheckedValues(defaultValues.checkedValues);
      setValueFilters(defaultValues.filters);
      liftState({
        min: '',
        max: '',
      });
    }
  }

  const clearOutInputBoxes = () => {
    if (maxValueInputBox || minValueInputBox){
      setMinValueInputBox('');
      setMaxValueInputBox('');
    }
  }

  const getCheckboxes = list.map((item, idx) => {
    let text, value;

    if (idx === 0) {
      text = `Less than ${list[idx + 1]}${symbol}`;
      value = `${item},${list[idx + 1]}`;
    } else if (idx === list.length - 1) {
      text = `More than ${item}${symbol}`;
      value = `${item},Infinity`;
    } else {
      text = `From ${item}${symbol} up to ${list[idx + 1]}${symbol}`;
      value = `${item},${list[idx + 1]}`;
    }

    return (
      <Grid item xs={12} key={`${item}${idx}`}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checkedValues[idx]}
              value={value}
              onFocus={clearOutInputBoxes}
              onChange={(event) => handleCheckbox(event, idx)}
            />
          }
          sx={{
          // Compact spacing for precise pointers
            '@media (pointer: fine)': {
              my: 0,
              '& .MuiCheckbox-root': {
                padding: '4px',
              },
            }
          }}
          label={text}
        />
      </Grid>
    );
  });

  const labelStyle = {
    color: isUsingFilter ? '#1976D2' : '',
  };

  return (
    <FormControl component="fieldset" style={{ borderColor: isUsingFilter ? '#1976D2' : '' }} sx={{ width: '100%' }}>
      <FormLabel component="legend" style={labelStyle}>{icon}{translations.title}</FormLabel>
      <Grid container>{getCheckboxes}</Grid>
      <Stack className={'tc'} sx={{ width: 'auto', maxWidth: '20rem' }} style={{marginInline: 'auto'}}>
        <TextField
          type="search"
          label={translations.labelMin}
          name="min"
          value={String(minValueInputBox)}
          onFocus={clearOutCheckboxes}
          onChange={handleInputBox}
          inputMode="numeric"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">{symbol}</InputAdornment>,
            },
          }}
        /> {translations.separator}
        <TextField
          sx={{ width: 'auto', maxWidth: '20rem' }}
          type="search"
          label={translations.labelMax}
          name="max"
          value={String(maxValueInputBox)}
          onFocus={clearOutCheckboxes}
          onChange={handleInputBox}
          inputMode="numeric"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">{symbol}</InputAdornment>,
            },
          }}
        />
      </Stack>
    </FormControl>
  )
}
